import { useEffect } from 'react';

import { useUser } from '@clerk/react';
import { TeamInvite, WebSocketEntityType } from '@this+that/models';

import { useTeamStore } from '../../../store/Team/useTeamStore';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

export function useTeamInvitesSync() {
  const { subscribe } = useWebSocket();
  const { user } = useUser();
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress;

  useEffect(() => {
    const normalizedEmail = currentUserEmail?.toLowerCase();

    // An invite event now reaches the team's admins too, not just the invitee
    // (see resolveInviteRecipients). `pendingInvites` is "invites I received" —
    // it drives the sidebar badge and the accept/decline prompt in
    // useAlertQueue, so writing someone else's invite into it would prompt an
    // admin to accept an invitation addressed to another person.
    const isAddressedToMe = (invite: TeamInvite) =>
      !!normalizedEmail &&
      invite.inviteeEmail?.toLowerCase() === normalizedEmail;

    // Both collections CREATE their bucket when absent, stamped with a fresh
    // loadedAt — which their loaders then read as a warm cache and skip the
    // real fetch, leaving one item where there are several. Only mirror into a
    // collection that has actually been loaded; if it has not, the pending
    // fetch will pick this invite up anyway.
    const hasLoadedTeamInvites = (teamId: string) =>
      Boolean(useTeamStore.getState().invitesByTeamId[teamId]);

    const hasLoadedMyInvites = () =>
      useTeamStore.getState().pendingInvites !== null;

    const unsubscribeInsert = subscribe<TeamInvite>(
      WebSocketEntityType.TeamInvite,
      WebSocketEventType.INSERT,
      ({ item }) => {
        if (!item?.teamId) {
          return;
        }
        if (isAddressedToMe(item) && hasLoadedMyInvites()) {
          useTeamStore.getState().prependPendingInvite(item);
        }
        if (hasLoadedTeamInvites(item.teamId)) {
          useTeamStore.getState().upsertTeamInvites(item.teamId, [item]);
        }
      },
    );

    const unsubscribeRemove = subscribe<TeamInvite>(
      WebSocketEntityType.TeamInvite,
      WebSocketEventType.REMOVE,
      ({ item }) => {
        if (!item?.teamId) {
          return;
        }
        if (isAddressedToMe(item)) {
          useTeamStore.getState().removePendingInvite(item.teamId);
        }
        useTeamStore
          .getState()
          .removeTeamInvite(item.teamId, item.inviteeEmail);
      },
    );

    return () => {
      unsubscribeInsert();
      unsubscribeRemove();
    };
  }, [subscribe, currentUserEmail]);
}
